<template>
    <chats-find
        v-slot="{
            data: chat,
            error: find_error,
            has_loaded,
            find
        }"
        :params="{
            chat: $route.params.chat
        }"
        :include="[
            `messages(paginate:1,${per_page}|include:files(paginate:1,10))`,
            'landlords(paginate:1,5)',
            'tenants(paginate:1,5)'
        ]"
        @success="onChatsFindSuccess"
    >
        <div v-if="!has_loaded || find_error">
            <base-loading
                :error="find_error"
                :retry="find"
                class="py-6"
            />

            <error-modal
                v-if="find_error"
                :error="find_error"
            />
        </div>

        <div
            v-else
            class="h-full"
        >
            <div class="h-full mb-6 bg-white rounded shadow">
                <div class="flex flex-col h-full">
                    <header class="flex items-center flex-none px-4 border-b h-18 sm:px-6">
                        <div class="flex-grow">
                            <h1 class="text-lg font-normal truncate">
                                <with-variables
                                    v-slot="{
                                        personas,
                                        total
                                    }"
                                    :variables="{
                                        personas: _.filter([...chat.landlords.data, ...chat.tenants.data], persona => !isMe(persona)),
                                        total: chat.landlords.meta.total + chat.tenants.meta.total - 1,
                                    }"
                                >
                                    {{ personas | comma-delimited({
                                        callback: persona => {
                                            return persona.name
                                        },
                                        total
                                    }) }}
                                </with-variables>
                            </h1>
                        </div>

                        <div
                            v-if="false"
                            class="flex-none w-6 h-6"
                        >
                            <button
                                class="w-full h-full btn btn-none"
                            >
                                <base-svg
                                    name="icon-dots-horizontal"
                                    class="w-full h-full"
                                />
                            </button>
                        </div>
                    </header>

                    <messages-got
                        v-slot="{
                            data: messages
                        }"
                        :sid="sid"
                    >
                        <messages-get
                            v-slot="{
                                is_loading,
                                error: get_error,
                                meta,
                                get
                            }"
                            :params="{
                                chat: chat.uuid
                            }"
                            :include="[
                                'files(paginate:1,10)'
                            ]"
                            :per-page="per_page"
                            :filters="[
                                messages.length > 0 ? `whereBeforeMessage=${messages[0].uuid}` : '',
                            ]"
                            :preloaded="chat.messages"
                            :preloaded-emit="false"
                            :get-on-created="false"
                            @success="onMessagesGetSuccess"
                        >
                            <div class="relative flex-grow">
                                <div
                                    v-if="messages.length === 0"
                                    class="p-3 italic text-center text-grey"
                                >
                                    There's nothing here!
                                </div>

                                <template v-else>
                                    <with-scrolling
                                        ref="withScrolling"
                                        :scroll-to-bottom-on-created="true"
                                    >
                                        <div class="absolute inset-0 p-3 overflow-x-auto scrollbar-hidden">
                                            <div class="text-center">
                                                <button
                                                    v-if="meta.last_page > 1"
                                                    class="relative btn"
                                                    @click.prevent="get"
                                                >
                                                    <div
                                                        class="flex items-center justify-center w-full h-full"
                                                        :class="is_loading ? 'invisible' : ''"
                                                    >
                                                        Load more
                                                    </div>

                                                    <div
                                                        class="absolute inset-0 items-center"
                                                        :class="is_loading ? 'flex' : 'hidden'"
                                                    >
                                                        <base-svg
                                                            name="icon-loading"
                                                            class="w-6 h-6 mx-auto fill-primary-grey-dark"
                                                        />
                                                    </div>
                                                </button>
                                            </div>

                                            <div
                                                v-for="message in messages"
                                                :key="message.uuid"
                                                class="clearfix mt-4"
                                            >
                                                <div
                                                    v-if="_.has(message, 'files.meta.count', 0) && message.files.meta.count > 0"
                                                    class="px-2 mb-1"
                                                >
                                                    <grid-layout
                                                        :class="{
                                                            'justify-end': isMe(message.persona)
                                                        }"
                                                    >
                                                        <grid-layout-item
                                                            v-for="file in message.files.data"
                                                            :key="file.uuid"
                                                            class="w-1/2 px-1 mb-1 md:w-1/3"
                                                        >
                                                            <file-card
                                                                :file="file"
                                                                :width="300"
                                                                :height="250"
                                                                class="aspect-ratio-4/3"
                                                            />
                                                        </grid-layout-item>
                                                    </grid-layout>
                                                </div>

                                                <div class="clearfix">
                                                    <div
                                                        class="relative inline-block p-3 px-4 overflow-hidden rounded"
                                                        :class="isMe(message.persona) ? 'float-right bg-blue-lightest ml-8 md:ml-24' : 'float-left bg-grey-lightest mr-8 md:mr-24'"
                                                    >
                                                        <div
                                                            class="absolute inset-x-0 top-0 rounded-t h-2px"
                                                            :style="{
                                                                'background-color': _.strToHsl(message.persona.uuid, 80, 80)
                                                            }"
                                                        />

                                                        <header class="mb-1 text-sm">
                                                            <span class="mr-2 font-semibold uppercase text-grey-darkest">
                                                                {{ isMe(message.persona) ? 'Me' : message.persona.first_name }}
                                                            </span>

                                                            <span class="mr-2 text-grey-dark">
                                                                <template v-if="$moment(message.created_at).isAfter($moment().subtract(1, 'day'))">
                                                                    {{ message.created_at | moment('HH:mm') }}
                                                                </template>

                                                                <template v-else>
                                                                    {{ message.created_at | moment('HH:mm DD/MM/YYYY') }}
                                                                </template>
                                                            </span>

                                                            <template v-if="_.get(message, 'context.action') === 'interested-in-listing'">
                                                                <listings-find
                                                                    :params="{
                                                                        listing: message.context.uuid
                                                                    }"
                                                                    :include="['property']"
                                                                    v-slot="{
                                                                        data
                                                                    }"
                                                                >
                                                                    <span
                                                                        v-if="data"
                                                                        class="text-grey-dark"
                                                                    >
                                                                        About
                                                                        <a
                                                                            target="_blank"
                                                                            :href="data.links.www.show"
                                                                            class="text-blue"
                                                                        >
                                                                            {{ data.property.name | limit(15) }}
                                                                        </a>
                                                                    </span>
                                                                </listings-find>
                                                            </template>
                                                        </header>

                                                        <div
                                                            class="text-grey-darker"
                                                            v-emoji
                                                        >
                                                            {{ message.text }}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <error-modal
                                                v-if="get_error"
                                                :error="get_error"
                                            />
                                        </div>
                                    </with-scrolling>
                                </template>
                            </div>
                        </messages-get>
                    </messages-got>

                    <messages-store
                        v-slot="{
                            sid: store_sid,
                            is_loading: is_saving,
                            error: store_error,
                            store
                        }"
                        :include="[
                            'files(paginate:1,10)'
                        ]"
                        @success="onMessagesStoreSuccess"
                    >
                        <uploads-store
                            v-model="uploads"
                            :url="$laroute.route('api.uploads.store')"
                            :max-files="10"
                            :max-size="20"
                            :disabled="is_saving"
                            accepted-files=".png,.jpg"
                            v-slot="{
                                removeFile,
                                reset,
                                open,
                                error: upload_error
                            }"
                        >
                            <base-form
                                class="flex-none border-t rounded-b bg-grey-bg"
                                @submit="() => {
                                    $validator.forgive('text', store_sid)

                                    if (!form.text && form.uploads.length === 0) {
                                        return
                                    }

                                    store({
                                        chat: chat.uuid,
                                        ...form
                                    })

                                    form.text = ''

                                    reset()
                                }"
                            >
                                <div
                                    v-if="uploads.length > 0"
                                    class="p-3 overflow-x-scroll whitespace-no-wrap scrollbar-hidden"
                                >
                                    <input-uploads-item
                                        v-for="upload in uploads"
                                        :key="upload.id"
                                        :is-thumbnail="true"
                                        :name="upload.upload.filename"
                                        :status="upload.status"
                                        :img-src="upload.dataURL"
                                        @remove="removeFile(upload)"
                                        class="inline-block w-1/3 mr-3 md:w-1/5 last-child:mr-0"
                                    />
                                </div>

                                <div class="flex items-center p-3">
                                    <form-item
                                        :sid="store_sid"
                                        name="text"
                                        class="mb-0! md:mr-2 flex-grow"
                                    >
                                        <input-text
                                            v-model="form.text"
                                            ref="input"
                                            name="text"
                                            placeholder="Write a message..."
                                            class="h-12"
                                            @focus="show_actions = false; scrollToBottom()"
                                            @blur="show_actions = true"
                                        />
                                    </form-item>

                                    <div
                                        class="flex-none h-12 md:block"
                                        :class="{
                                            'hidden': !show_actions
                                        }"
                                    >
                                        <button
                                            type="button"
                                            class="flex-none w-8 h-12 px-2 py-3 ml-1 md:w-10 focus:outline-none"
                                            @click.prevent="open"
                                        >
                                            <base-svg
                                                name="icon-document-add"
                                                class="w-6 h-6"
                                            />
                                        </button>
                                    </div>

                                    <button
                                        type="submit"
                                        class="flex-none w-8 h-12 px-2 py-3 ml-1 mr-1 md:w-10 focus:outline-none"
                                    >
                                        <base-svg
                                            name="icon-arrow-thin-right-circle"
                                            class="w-6 h-6 fill-primary-white fill-secondary-blue"
                                        />
                                    </button>
                                </div>

                                <error-modal
                                    v-if="store_error && $error.is(store_error, { exclude: [422] })"
                                    :error="store_error"
                                />

                                <error-modal
                                    v-else-if="upload_error && $error.is(upload_error, { exclude: [422] })"
                                    :error="upload_error"
                                />
                            </base-form>
                        </uploads-store>
                    </messages-store>
                </div>
            </div>
        </div>
    </chats-find>
</template>

<script>
    import _ from 'lodash'
    import * as types from '../../../../store/types'
    import ChatsFind from '../../../../components/chats-find'
    import FileCard from '../../../../components/file-card'
    import InputUploadsItem from '../../../../components/input-uploads-item'
    import ListingsFind from '../../../../components/listings-find'
    import MessagesGet from '../../../../components/messages-get'
    import MessagesGot from '../../../../components/messages-got'
    import MessagesStore from '../../../../components/messages-store'
    import UploadsStore from '../../../../components/uploads-store'

    export default {
        components: {
            ChatsFind,
            FileCard,
            InputUploadsItem,
            ListingsFind,
            MessagesGet,
            MessagesGot,
            MessagesStore,
            UploadsStore
        },
        data () {
            return {
                sid: _.uuid(),
                per_page: 25,
                show_actions: true,
                form: {
                    text: '',
                    uploads: []
                },
                uploads: []
            }
        },
        watch: {
            uploads () {
                this.form.uploads = _.chain(this.uploads)
                    .filter({
                        status: 'success'
                    })
                    .map(file => {
                        return _.extend(JSON.parse(file.xhr.response).data, {
                            upload: file
                        })
                    })
                    .value()
            }
        },
        computed: {
            me () {
                return _.get(_.preload('tenant') || _.preload('landlord'), 'data')
            }
        },
        mounted () {
            this.$echo.join(`chat-${this.$route.params.chat}`)
                .listen('message-made-event', ({ data: message }) => {
                    this.appendMessage(message)
                })
        },
        beforeRouteLeave (to, from, next) {
            this.$echo.leave(`chat-${this.$route.params.chat}`)

            next()
        },
        methods: {
            onChatsFindSuccess ({ data: chat }) {
                _.each(chat.messages.data, message => {
                    this.prependMessage(message)
                })

                this.sendReadReceipt()
            },
            onMessagesGetSuccess (items) {
                this.$nextTick(() => {
                    this.$refs.withScrolling.dontMove(() => {
                        _.each(items.data, message => {
                            this.prependMessage(message)
                        })
                    })
                })
            },
            onMessagesStoreSuccess ({ data: message }) {
                this.appendMessage(message)
            },
            prependMessage (message) {
                this.$store.commit(types.messages.mutations.prepend, {
                    sid: this.sid,
                    item: message
                })
            },
            appendMessage (message) {
                this.$store.commit(types.messages.mutations.append, {
                    sid: this.sid,
                    item: message
                })

                this.scrollToBottom()

                this.sendReadReceipt()
            },
            sendReadReceipt () {
                this.$store.dispatch(types.chats.actions.read, {
                    params: {
                        chat: this.$route.params.chat
                    }
                })
                    .catch(error => {
                        console.error(error)
                    })
            },
            scrollToBottom (tries = 0) {
                this.$nextTick(() => {
                    if (this.$refs.withScrolling) {
                        if (this.$refs.withScrolling.bottom < 100) {
                            this.$refs.withScrolling.scrollToBottom()
                        }
                    } else if (tries < 3) {
                        console.log('failed to scroll, trying again', tries)

                        _.debounce(() => {
                            this.scrollToBottom(tries++)
                        }, 250)
                    }
                })
            },
            isMe (persona) {
                return this.me &&
                    this.me.object === persona.object &&
                    this.me.uuid === persona.uuid
            }
        }
    }
</script>
